import React, { ReactNode } from 'react'
import { useSelector } from 'react-redux'
import { Box, useMediaQuery, useTheme } from '@mui/material'
import { useNavigate } from 'react-router-dom'

import {
  DiscountBanner,
  ImageContainer,
  ImageWrapper,
  ProductCardContainer,
  ProductCardContent,
  ProductContentContainer,
  ProductType,
  TopDownWrapper,
} from './ProductCardParent'
import { RemoveButton } from './RemoveButton'
import { useProductStatus } from '../../utils/useProductStatus'
import TestIds from '../../../../testIds'
import PlaceholderImage from '../../../../assets/images/image-placeholder.png'
import { selectCurrentFarmerAccount } from '../../../azure-b2c/storage'
import { ProductHeaderDetails } from './ProductHeaderDetails'
import { ProductVariantDetails } from './ProductVariants'
import Card, { CardAction, CardTitle } from '../../../../components/Card'
import { getProductPrices } from '../../utils/getPrices'
import UrgentDeliveryUnavailableMessage from '../../../cart/components/UrgentDeliveryUnavailableMessage'
import { EditButton } from './EditButton'
import { openProductDetailsLink } from '../ProductDetails/utils/openProductDetails'
import { CartProductCardProps } from './CartProductCard'

interface CartItemCardProps extends CartProductCardProps {
  onRemove?: () => void
  renderSideContainer?: ReactNode
  renderQuickOrderButton?: ReactNode
  showImage?: boolean
  showUrgentDeliveryUnavailableMessage?: boolean
}

export const CartItemCard: React.FC<CartItemCardProps> = ({
  className,
  item,
  onChangeVariant,
  enableVariants,
  onRemove,
  handleOpenEditPriceModal,
  priceEditable = false,
  renderSideContainer,
  renderQuickOrderButton,
  showImage = true,
  showUrgentDeliveryUnavailableMessage = true,
}) => {
  const navigate = useNavigate()
  const currentAccount = useSelector(selectCurrentFarmerAccount)

  const theme = useTheme()
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'))

  const { noPriceAvailable } = useProductStatus(item.lifeCycleState, item.basePriceExcGst)
  const disableLink = enableVariants

  const setQueryParams = () => {
    openProductDetailsLink(item, false, ProductType.All, navigate)
  }

  const productDetailsNavigate = (e: React.MouseEvent<HTMLElement, MouseEvent> | null) => {
    e?.preventDefault()
    setQueryParams()
  }

  return (
    <ProductCardContainer
      className={className}
      data-testid={TestIds.feat.product.card.card}
      data-id={item.productId}
      data-line-id={item.lineId}
    >
      {item.discounts && (
        <DiscountBanner data-testid={TestIds.feat.product.card.discountBanner}>
          Special offer!
        </DiscountBanner>
      )}
      <UrgentDeliveryUnavailableMessage show={showUrgentDeliveryUnavailableMessage} />
      <Card
        data-testid={TestIds.feat.product.card.card}
        data-id={item.productId}
        data-line-id={item.productId}
        variant='outlined'
        disableLink={disableLink}
      >
        <ProductCardContent>
          {onRemove && <RemoveButton onRemove={onRemove} />}
          <ProductContentContainer>
            {showImage ? (
              <ImageWrapper>
                <ImageContainer
                  src={item.imageUrl}
                  placeholderImageUrl={PlaceholderImage}
                  data-testid={TestIds.feat.product.card.image}
                  alt=''
                />
              </ImageWrapper>
            ) : (
              <Box height={'40px'}></Box>
            )}
            <TopDownWrapper>
              <CardAction
                disabled={disableLink}
                tabIndex={-1}
                disableRipple
                sx={{ p: 0 }}
                onClick={(e) => productDetailsNavigate(e)}
              >
                <CardTitle
                  title={item.name}
                  showIcon={!enableVariants}
                  testId={TestIds.feat.product.card.name}
                />
                <ProductHeaderDetails
                  itemNumber={item.itemNumber}
                  items={getProductPrices({
                    item,
                    quantity: item.quantity,
                    isTfo: currentAccount?.hasTfoAccess,
                  })}
                  noPriceAvailable={noPriceAvailable}
                  productId={item.productId.toString()}
                />
              </CardAction>
              {priceEditable ? <EditButton onClick={handleOpenEditPriceModal} /> : null}
              <ProductVariantDetails
                key={'variants'}
                item={item}
                enableVariants={enableVariants}
                onChangeVariant={onChangeVariant}
              />
              {isSmall && renderQuickOrderButton}
            </TopDownWrapper>
          </ProductContentContainer>

          {renderSideContainer}
        </ProductCardContent>
      </Card>
    </ProductCardContainer>
  )
}
